/**
 * Suggestions waiting in the document: mytender.io's tracked changes, from a
 * pull that replaced an answer or from io.ai's edits, grouped per question
 * (see document/review.ts). Shown at the bottom of both tabs whenever there
 * are any, with Reject all and Accept all, and Review / Next, which selects a
 * suggestion in Word (the whole answer for a question) and offers Accept and
 * Reject for it, since Word shows no accept/reject popup for a selection an
 * add-in makes. The list follows the document: it is re-read after the pane
 * changes the document and every few seconds while the pane is visible, so
 * changes resolved in Word itself drop out too. Hosts that can't list
 * revisions (before WordApi 1.6) show nothing; Word's Review tab still works.
 */
import { ChevronRightIcon } from "@/design/icons";
import { type FC, type ReactNode, useCallback, useEffect, useRef, useState } from "react";

import { cn } from "@/agents-v2/utils/cn";

import {
  type ReviewScope,
  type SelectedSuggestion,
  type Suggestion,
  clearReviewBox,
  listSuggestions,
  resolveSuggestion,
  reviewChanges,
  selectSuggestion,
} from "../../document/review";
import { documentCapabilities } from "../../document/snapshot";

const POLL_MS = 4000;

/**
 * The suggestions in scope, kept current; `refresh` re-reads now. One read
 * at a time, and none while `paused` (a pull is writing: its Word calls
 * must not queue behind the pane's reads).
 */
export const useSuggestions = (scope: ReviewScope, paused = false) => {
  const live = documentCapabilities().review;
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const inFlight = useRef(false);

  const refresh = useCallback(async () => {
    if (!live || pausedRef.current || inFlight.current) return;
    inFlight.current = true;
    try {
      setSuggestions(await listSuggestions(scopeRef.current));
    } catch {
      // Word busy (the user typing, a sync in flight): keep the last list.
    } finally {
      inFlight.current = false;
    }
  }, [live]);

  useEffect(() => {
    if (!live) return;
    void refresh();
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") void refresh();
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [live, refresh, scope.author]);
  // Read once a pause ends.
  useEffect(() => {
    if (!paused) void refresh();
  }, [paused, refresh]);

  return { live, suggestions, count: suggestions.length, refresh };
};

export const SuggestionsBar: FC<{
  suggestions: Suggestion[];
  scope: ReviewScope;
  onChanged: () => void;
  className?: string;
}> = ({ suggestions, scope, onChanged, className }) => {
  const count = suggestions.length;
  const revisions = suggestions.reduce((n, s) => n + s.revisions, 0);
  const allAnswers = suggestions.every((s) => s.inBlock);
  const unit = allAnswers ? (count === 1 ? "answer" : "answers") : count === 1 ? "place" : "places";
  const [busy, setBusy] = useState<"accept" | "reject" | "next" | "one" | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The suggestion selected in Word, resolved one at a time below.
  const [current, setCurrent] = useState<SelectedSuggestion | null>(null);
  useEffect(() => {
    if (count <= 0) {
      setCurrent(null);
      void clearReviewBox();
    }
  }, [count]);
  useEffect(() => () => void clearReviewBox(), []);
  if (count <= 0) return null;

  const resolveAll = async (action: "accept_all" | "reject_all") => {
    setBusy(action === "accept_all" ? "accept" : "reject");
    setError(null);
    try {
      await reviewChanges(action, scope);
      setCurrent(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Word couldn't resolve the suggestions");
    } finally {
      setBusy(null);
      onChanged();
    }
  };
  const review = async () => {
    setBusy("next");
    setError(null);
    try {
      setCurrent(await selectSuggestion(scope, current ? { afterKey: current.key } : {}));
    } catch {
      setError("Word couldn't select the next suggestion");
    } finally {
      setBusy(null);
    }
  };
  // Resolve the selected suggestion, then move straight to the one after it
  // (which now has its index).
  const resolveOne = async (action: "accept" | "reject") => {
    if (!current) return;
    setBusy("one");
    setError(null);
    try {
      const resolved = await resolveSuggestion(scope, current.key, action);
      if (!resolved) setError("That suggestion is no longer in the document.");
      // The one after it now has its index.
      setCurrent(await selectSuggestion(scope, { index: current.index }));
    } catch {
      setError("Word couldn't resolve that suggestion");
    } finally {
      setBusy(null);
      onChanged();
    }
  };

  return (
    <div
      role="status"
      className={cn("rounded-sm border border-accent/30 bg-accent-soft px-2.5 py-1.5 text-xs text-fg", className)}
    >
      <div className="flex items-center gap-1">
        <span className="size-1.5 shrink-0 rounded-full bg-accent" />
        <span className="ml-1 min-w-0 flex-1 truncate">
          <span className="font-semibold">{revisions}</span> {revisions === 1 ? "change" : "changes"} in {count} {unit}
        </span>
        <BarButton onClick={() => void review()} disabled={busy !== null}>
          {current ? "Next" : "Review"}
          <ChevronRightIcon className="size-3" />
        </BarButton>
        <BarButton onClick={() => void resolveAll("reject_all")} disabled={busy !== null}>
          {busy === "reject" ? "Rejecting…" : "Reject all"}
        </BarButton>
        <BarButton primary onClick={() => void resolveAll("accept_all")} disabled={busy !== null}>
          {busy === "accept" ? "Accepting…" : "Accept all"}
        </BarButton>
      </div>
      {current && (
        <div className="mt-1.5 flex items-center gap-1 border-t border-accent/20 pt-1.5">
          <span className="min-w-0 flex-1 truncate text-fg-2" title={current.text}>
            <span className="font-semibold text-fg">
              {current.index + 1}/{count} · {current.inBlock ? current.title : KIND[current.kind]} · {current.revisions}{" "}
              {current.revisions === 1 ? "change" : "changes"}
            </span>{" "}
            {current.text ? `“${current.text}”` : "an empty line"}
          </span>
          <BarButton onClick={() => void resolveOne("reject")} disabled={busy !== null}>
            Reject
          </BarButton>
          <BarButton primary onClick={() => void resolveOne("accept")} disabled={busy !== null}>
            Accept
          </BarButton>
        </div>
      )}
      {error && <div className="mt-1 text-danger">{error}</div>}
    </div>
  );
};

const KIND: Record<Suggestion["kind"], string> = {
  inserted: "Added",
  deleted: "Removed",
  changed: "Changed",
};

const BarButton: FC<{ primary?: boolean; disabled?: boolean; onClick: () => void; children: ReactNode }> = ({
  primary,
  disabled,
  onClick,
  children,
}) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    className={cn(
      "inline-flex h-6 shrink-0 items-center gap-0.5 rounded-sm px-2 text-xs font-semibold transition-colors duration-fast disabled:opacity-50",
      primary ? "bg-fg text-fg-inverse hover:bg-fg/90" : "text-fg-2 hover:bg-accent-soft-2 hover:text-fg"
    )}
  >
    {children}
  </button>
);
